import { useState } from 'react';
import { draftApi } from '@/services/api';
import { useNotificationStore } from '@/stores/notificationStore';

export interface UseDraftModificationReturn {
  modifyDraft: (
    draftId: string,
    currentContent: string,
    newContent: string,
    method: 'append' | 'insert',
    lineNumber?: number
  ) => Promise<string | null>;
  isModifying: boolean;
  error: string | null;
}

/**
 * Appends new content to the end of current content
 */
export function appendContent(current: string, newContent: string): string {
  return current.trim() + '\n\n' + newContent;
}

/**
 * Inserts new content at a specific line number (1-based)
 */
export function insertAtLine(current: string, newContent: string, lineNumber: number): string {
  const lines = current.split('\n');
  const totalLines = lines.length;

  // Validate line number
  let insertIndex = lineNumber - 1; // Convert to 0-based index
  if (insertIndex < 0) insertIndex = 0;
  if (insertIndex > totalLines) {
    // If line number is beyond end, append instead
    return appendContent(current, newContent);
  }

  // Split new content into lines and insert at the specified position
  const newContentLines = newContent.split('\n');
  lines.splice(insertIndex, 0, ...newContentLines);

  return lines.join('\n');
}

/**
 * Hook for modifying draft content with append or insert operations
 */
export function useDraftModification(): UseDraftModificationReturn {
  const [isModifying, setIsModifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addNotification } = useNotificationStore();

  /**
   * Modifies a draft by either appending or inserting content
   * Returns the new content on success, null on failure
   */
  const modifyDraft = async (
    draftId: string,
    currentContent: string,
    newContent: string,
    method: 'append' | 'insert',
    lineNumber?: number
  ): Promise<string | null> => {
    setIsModifying(true);
    setError(null);

    try {
      // Calculate the new content based on method
      let modifiedContent: string;
      if (method === 'append') {
        modifiedContent = appendContent(currentContent, newContent);
      } else if (method === 'insert') {
        if (lineNumber === undefined) {
          throw new Error('Line number is required for insert operation');
        }
        modifiedContent = insertAtLine(currentContent, newContent, lineNumber);
      } else {
        throw new Error(`Unknown modification method: ${method}`);
      }

      // Call the API to update the draft
      await draftApi.updateDraft(draftId, undefined, modifiedContent, undefined);

      // Success notification
      addNotification({
        type: 'success',
        title: 'Draft Updated',
        message: `Content ${method === 'append' ? 'appended to' : 'inserted into'} draft successfully`,
      });

      return modifiedContent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update draft';
      setError(errorMessage);

      // Error notification
      addNotification({
        type: 'error',
        title: 'Failed to Update Draft',
        message: errorMessage + '. Please try again.',
        duration: 4000,
      });

      console.error('Error modifying draft:', err);
      return null;
    } finally {
      setIsModifying(false);
    }
  };

  return {
    modifyDraft,
    isModifying,
    error,
  };
}
